import { useMemo } from "react";
import { CollectionResponse, CollectionItem, Collection } from "@/api";
import { ITEM_TYPE_COLLECTION, ITEM_TYPE_VIDEO } from "@/const";
import { useFullItems } from "@/hooks";

// Types for modules and course structure
export interface SimpleModule {
  id: string;
  title: string;
  lessons: CollectionItem[];
}

export interface Module {
  id: string;
  title: string;
  subcategories: SimpleModule[];
  lessons: CollectionItem[];
}

export interface Course {
  categories: Module[];
  hasSubcategories: boolean;
  lessons: CollectionItem[];
}

/**
 * Returns a structured representation of a course, with modules, submodules, and lessons.
 * - categories: top-level modules (with possible subcategories)
 * - hasSubcategories: true if any module has subcategories
 * - lessons: top-level lessons (not inside a module)
 */
export const useCourse = (
  collection: CollectionResponse | undefined,
): Course => {
  const childCollections = collection?.ChildCollections ?? {};
  const childVideos = collection?.ChildVideos ?? {};
  const { getFullItem } = useFullItems({
    videos: childVideos,
    collections: childCollections,
  });

  return useMemo(() => {
    const categories: Module[] = [];
    const lessons: CollectionItem[] = [];
    let hasSubcategories = false;

    if (!collection || !Array.isArray(collection.Items)) {
      return { categories, lessons, hasSubcategories };
    }

    for (const item of collection.Items) {
      if (item.Type === ITEM_TYPE_VIDEO) {
        // Top-level lesson
        lessons.push(item);
      } else if (item.Type === ITEM_TYPE_COLLECTION) {
        const moduleCollection = getFullItem(item) as Collection;
        const module: Module = {
          id: moduleCollection.TabId?.toString() ?? "",
          title: moduleCollection.Name ?? "Untitled",
          subcategories: [],
          lessons: [],
        };

        for (const moduleItem of moduleCollection.Items ?? []) {
          if (moduleItem.Type === ITEM_TYPE_VIDEO) {
            module.lessons.push(moduleItem);
          } else if (moduleItem.Type === ITEM_TYPE_COLLECTION) {
            // Subcategory (submodule)
            const submoduleCollection = getFullItem(moduleItem) as Collection;
            const subcategory: SimpleModule = {
              id: submoduleCollection.TabId?.toString() ?? "",
              title: submoduleCollection.Name ?? "Untitled",
              lessons: [],
            };
            for (const submoduleItem of submoduleCollection.Items ?? []) {
              if (submoduleItem.Type === ITEM_TYPE_VIDEO) {
                subcategory.lessons.push(submoduleItem);
              }
            }
            module.subcategories.push(subcategory);
          }
        }

        if (module.subcategories.length > 0) {
          hasSubcategories = true;
        }
        categories.push(module);
      }
    }

    return { categories, lessons, hasSubcategories };
  }, [collection, getFullItem]);
};
